import React, { useState, useEffect } from "react";
import { makeStyles, Typography, Box } from "@material-ui/core";
import PropTypes from "prop-types";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import { getCompetitionPositioningData } from "../../viewServices/apiCalls";
import {
  CardLoadingState,
  CardErrorState,
  CardEmptyState,
  CardNoDataState,
} from "../shared";

const MIN_HEIGHT = 180;

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  placeholderContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: MIN_HEIGHT,
    backgroundColor: "#FAFAFA",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  placeholderLabel: {
    fontSize: "1.75rem",
    fontWeight: 300,
    color: grayColor[10],
    marginBottom: theme.spacing(1.5),
  },
  dataContainer: {
    padding: theme.spacing(1),
    backgroundColor: "#FAFAFA",
    borderRadius: theme.shape.borderRadius,
    minHeight: MIN_HEIGHT,
  },
}));

export default function CardCompetitionPositioning({ programId, filters }) {
  const classes = useStyles();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(!!programId);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!programId) {
        setIsLoading(false);
        setData(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await getCompetitionPositioningData(
          programId,
          filters,
        );
        setData(response);
      } catch (err) {
        console.error("Error fetching Competition Positioning data:", err);
        setError(err.message || "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [programId, filters]);

  // Loading state
  if (isLoading) {
    return <CardLoadingState minHeight={MIN_HEIGHT} />;
  }

  // Error state
  if (error) {
    return <CardErrorState message={error} minHeight={MIN_HEIGHT} />;
  }

  // Empty state (no program selected)
  if (!programId) {
    return <CardEmptyState minHeight={MIN_HEIGHT} />;
  }

  // No data state - show placeholder (visualization in development)
  if (!data) {
    return (
      <CardNoDataState
        title="Nenhum dado de competição disponível"
        subtitle="Verifique os scenarios do program"
        minHeight={MIN_HEIGHT}
      />
    );
  }

  // When data is available, show placeholder for future visualization
  return (
    <Box className={classes.placeholderContent}>
      <Typography variant="caption" className={classes.placeholderLabel}>
        MK.1
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Dados carregados - Visualização em desenvolvimento
      </Typography>
    </Box>
  );
}

CardCompetitionPositioning.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
};
